import { Container, Graphics, Sprite, Text } from 'pixi.js';
import type { WhackOffer, WhackPop, WhackState, WhackTap } from '@goodtime/shared';
import { AudioBus } from '../../audio/AudioBus';
import {
  bandOf,
  bandPay,
  fasterAt,
  holeAt,
  missedBy,
  popAt,
  roundEnd,
  streakWords,
  tapsArg,
  WHACK_EXPLAIN,
  whackLead,
  whackWon,
  type WhackStage,
} from '../../logic/minigames/whack';
import { GAME_SOUNDS } from '../../logic/minigames/sounds';
import { animate, COLOURS, displayStyle, ease, prefersReducedMotion } from '../board/ui';
import { CounterRow, gridCells } from './pieces';
import { TurnGameDialog } from './TurnGameDialog';
import { WhackMeter } from './WhackMeter';
import type { MiniGameDeps } from './types';
import type { GameView } from '../../logic/minigames/core';

type WhackReveal = WhackPop[] | number;

/** The hole art's opening: its centre and radii, as fractions of the art
 *  (minigame/whack/hole.png, 384x192). The mole shows above the opening's
 *  middle and inside it, so it comes up out of the hole. */
const OPENING = { cx: 0.5, cy: 0.46, rx: 0.34, ry: 0.2 };
/** The countdown before the round: each beat this long. */
const BEAT_MS = 620;
/** Slack after the last mole goes down before the whistle. */
const AFTER_MS = 150;

interface HoleView {
  root: Container;
  hole: Sprite;
  drawn: Graphics;
  /** The mole, clipped by `mask` so it rises out of the hole. */
  mole: Sprite;
  moleDrawn: Graphics;
  mask: Graphics;
  /** Where the mole stands when fully up, and how tall it is. */
  moleY: number;
  moleH: number;
}

/**
 * Whack a mole, a speed round. The board shows nine holes, the prize
 * ladder (a lamp per hit, bigger lamps where the prize steps up) and the
 * counters. START asks the server for the round's schedule; after 3, 2, 1,
 * GO! the moles pop up on it, coming faster as the round goes (FASTER!),
 * and every hit lights a lamp, bonks the mole and grows the streak. The
 * whistle ends it, the taps go to the server, and it pays the band of hits
 * it counted. The first band is a loss, said plainly; a bigger band is a
 * win with confetti.
 */
export class WhackDialog extends TurnGameDialog<WhackOffer, WhackState, WhackReveal> {
  private holes: HoleView[] = [];
  private readonly meter: WhackMeter;
  private readonly counters: CounterRow;
  private readonly banner: Text;
  private readonly floaters = new Container();
  private stage: WhackStage = 'ready';
  private pops: WhackPop[] = [];
  private end = 0;
  /** performance.now() at GO, and when the start's answer arrived. */
  private t0 = 0;
  private answeredAt = 0;
  private readonly hit = new Set<number>();
  private readonly hitAt = new Map<number, number>();
  private readonly risen = new Set<number>();
  private taps: WhackTap[] = [];
  private streak = 0;
  private missed = 0;
  private faster: number[] = [];
  private finishing = false;
  /** The finish failed to reach the server: TRY AGAIN sends it again. */
  private retry = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private gridBox = { x: 0, y: 0, w: 0, h: 0 };
  /** The width the meter was built for (0: build it at the next render). */
  private meterWidth = 0;

  constructor(deps: MiniGameDeps) {
    super(deps, 'whack', 'WHACK A MOLE');
    this.meter = new WhackMeter(
      () => this.deps.texture('road:whack_bezel') ?? this.deps.texture('road:light_bezel'),
      () => this.explain(WHACK_EXPLAIN.meter(this.offer?.setup), { x: this.contentWidth / 2, y: this.meter.y + this.meter.rowHeight }, true),
    );
    this.counters = new CounterRow(['Hits', 'Prize', 'Time'], (at) => this.explain(WHACK_EXPLAIN.counters(this.offer?.setup), at));
    this.banner = new Text({ text: '', style: displayStyle(64) });
    this.banner.anchor.set(0.5);
    this.banner.visible = false;
    this.banner.eventMode = 'none';
    this.floaters.eventMode = 'none';
    this.content.addChild(this.meter, this.counters);
  }

  protected begin(offer: WhackOffer): void {
    this.holes.forEach((h) => h.root.destroy({ children: true }));
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.stage = 'ready';
    this.pops = [];
    this.end = 0;
    this.hit.clear();
    this.hitAt.clear();
    this.risen.clear();
    this.taps = [];
    this.streak = 0;
    this.missed = 0;
    this.faster = [];
    this.finishing = false;
    this.retry = false;
    this.floaters.removeChildren().forEach((c) => c.destroy());
    this.banner.visible = false;
    const n = offer.setup?.holes ?? 9;
    this.holes = Array.from({ length: n }, (_, i) => {
      const root = new Container();
      const hole = new Sprite();
      hole.anchor.set(0.5);
      const drawn = new Graphics();
      const mole = new Sprite();
      mole.anchor.set(0.5, 1);
      const moleDrawn = new Graphics();
      const mask = new Graphics();
      const moleBox = new Container();
      moleBox.addChild(moleDrawn, mole);
      moleBox.mask = mask;
      root.addChild(drawn, hole, moleBox, mask);
      root.eventMode = 'static';
      root.cursor = 'pointer';
      // A whack lands as the finger goes down: every millisecond counts.
      root.on('pointerdown', () => this.onPiece(i));
      this.content.addChild(root);
      return { root, hole, drawn, mole, moleDrawn, mask, moleY: 0, moleH: 40 };
    });
    this.content.addChild(this.floaters, this.banner);
    this.meterWidth = 0;
  }

  // ---- taps ---------------------------------------------------------------

  protected onPiece(index: number): void {
    if (this.stage === 'ready') {
      const h = this.holes[index];
      if (h && this.game.phase === 'play') this.explain(WHACK_EXPLAIN.meter(this.offer?.setup), { x: h.root.x, y: h.root.y - 30 });
      return;
    }
    if (this.stage !== 'round') return;
    const t = performance.now() - this.t0;
    const pop = popAt(this.pops, index, t, this.hit);
    if (pop < 0) {
      // A whack at an empty hole breaks the streak.
      this.streak = 0;
      return;
    }
    this.hit.add(pop);
    this.hitAt.set(pop, t);
    this.taps.push({ pop, tMs: t });
    this.streak += 1;
    const before = bandOf(this.setup, this.hit.size - 1);
    const after = bandOf(this.setup, this.hit.size);
    AudioBus.shared.play(GAME_SOUNDS.bonk, { volume: 0.8 });
    this.meter.set(this.hit.size);
    const h = this.holes[index];
    if (h) {
      void animate(160, (k) => h.root.scale.set(1 + 0.08 * Math.sin(k * Math.PI), 1 - 0.06 * Math.sin(k * Math.PI)));
      this.float(h, after > before ? 'PRIZE UP!' : streakWords(this.streak) || '+1', after > before);
    }
    if (after > before) window.setTimeout(() => AudioBus.shared.play(GAME_SOUNDS.prizeUp, { volume: 0.8 }), 90);
    this.paintCounters();
  }

  protected override actionLabel(): string | null {
    if (this.stage === 'ready') return 'START';
    // The finish didn't reach the server: send the same taps again.
    if (this.stage === 'counting' && this.retry && !this.finishing && this.game.phase === 'play') return 'TRY AGAIN';
    return null;
  }

  protected override onActionTap(): void {
    if (this.stage === 'ready') {
      if (this.game.phase === 'play' && this.game.step > 0 && this.pops.length === 0) {
        // The server started the round but its answer was lost: ask again.
        this.replay(0, 'start');
        return;
      }
      this.act('start');
      return;
    }
    if (this.stage === 'counting') this.sendFinish();
  }

  protected override leadMinLines(): number {
    return this.game.phase !== 'over' ? 2 : 1;
  }

  protected override keepButtonRoom(): boolean {
    return this.stage === 'countdown' || this.stage === 'round' || this.stage === 'counting';
  }

  protected override wonPrize(): boolean {
    return whackWon(this.setup, this.serverHits());
  }

  protected override counterValues(): string[] | null {
    if (!this.counters.visible) return null;
    return [`Hits ${this.hit.size}`, `Prize ${bandPay(this.setup, this.hit.size)}`, `Time ${this.secondsLeft()}`];
  }

  // ---- the server's answers ---------------------------------------------

  protected override onAnswered(prev: GameView<WhackState, WhackReveal>, next: GameView<WhackState, WhackReveal>): void {
    const last = next.last;
    if (Array.isArray(last) && last !== prev.last && this.stage === 'ready') {
      this.pops = last;
      this.end = roundEnd(last);
      this.faster = fasterAt(last);
      this.answeredAt = performance.now();
      void this.countdown();
      return;
    }
    if (next.phase === 'over') {
      this.finishing = false;
      this.stage = 'over';
      this.banner.visible = false;
      // The server's count is the one that pays.
      this.meter.set(Math.min(this.serverHits(), Math.max(...(this.setup.bands.length ? this.setup.bands : [0]))));
      this.holes.forEach((h) => this.showMole(h, 0, false));
      return;
    }
    // A refusal or a lost answer.
    if (this.stage === 'counting' && this.finishing) {
      this.finishing = false;
      this.retry = true;
    }
  }

  private get setup() {
    return this.offer?.setup ?? { holes: 9, pops: 24, minMs: 8000, bands: [0], pays: [0] };
  }

  private serverHits(): number {
    const s = this.game.state;
    return s?.hits ?? (typeof this.game.last === 'number' ? this.game.last : this.hit.size);
  }

  // ---- the round ----------------------------------------------------------

  private async countdown(): Promise<void> {
    this.stage = 'countdown';
    this.render();
    for (const word of ['3', '2', '1']) {
      AudioBus.shared.play(GAME_SOUNDS.countdown, { volume: 0.7 });
      await this.showBanner(word, BEAT_MS, COLOURS.cream);
      if (this.stage !== 'countdown') return;
    }
    AudioBus.shared.play(GAME_SOUNDS.go, { volume: 0.8 });
    this.t0 = performance.now();
    this.stage = 'round';
    this.render();
    void this.showBanner('GO!', 500);
  }

  protected override animate(deltaMs: number): void {
    this.meter.tick(deltaMs, this.clock);
    if (this.stage !== 'round') return;
    const t = performance.now() - this.t0;
    this.pops.forEach((p, i) => {
      if (t >= p.atMs && !this.risen.has(i)) {
        this.risen.add(i);
        AudioBus.shared.play(GAME_SOUNDS.molePop, { volume: 0.3 });
      }
    });
    this.holes.forEach((h, i) => {
      const s = holeAt(this.pops, i, t, this.hitAt);
      this.showMole(h, s.up, s.dazed);
    });
    const missed = missedBy(this.pops, t, this.hit);
    if (missed > this.missed) {
      this.missed = missed;
      this.streak = 0;
    }
    if (this.faster.length && t >= this.faster[0]!) {
      this.faster.shift();
      AudioBus.shared.play(GAME_SOUNDS.faster, { volume: 0.55 });
      void this.showBanner('FASTER!', 700, COLOURS.gold, 'top');
    }
    this.paintCounters();
    if (t >= this.end + AFTER_MS) this.whistle();
  }

  /** The round is over: the whistle, then the taps go to the server once
   *  it has run its length by the server's clock too. */
  private whistle(): void {
    this.stage = 'counting';
    AudioBus.shared.play(GAME_SOUNDS.whistle, { volume: 0.8 });
    this.holes.forEach((h) => this.showMole(h, 0, false));
    void this.showBanner('TIME!', 900);
    this.render();
    const wait = Math.max(0, this.setup.minMs + 300 - (performance.now() - this.answeredAt));
    this.timer = setTimeout(() => this.sendFinish(), Math.max(wait, 600));
  }

  private sendFinish(): void {
    if (this.finishing || this.game.phase !== 'play') return;
    this.finishing = true;
    this.retry = false;
    this.timer = null;
    this.act('finish', tapsArg(this.taps));
  }

  // ---- drawing ------------------------------------------------------------

  private showMole(h: HoleView, up: number, dazed: boolean): void {
    const e = prefersReducedMotion() ? (up > 0 ? 1 : 0) : ease.outCubic(up);
    const y = h.moleY + (1 - e) * h.moleH * 1.05;
    h.mole.y = y;
    h.moleDrawn.y = y - h.moleY;
    h.mole.visible = up > 0 && !!h.mole.texture && h.mole.texture.width > 1;
    h.moleDrawn.visible = up > 0 && !h.mole.visible;
    const tex = this.deps.texture(dazed ? 'road:whack_bonked' : 'road:whack_mole');
    if (tex && h.mole.texture !== tex) {
      h.mole.texture = tex;
      h.mole.width = h.mole.height = h.moleH;
    }
  }

  /** Big words over the field: the countdown and TIME! in its middle;
   *  FASTER! smaller, at its top, so it never hides the moles for long. */
  private async showBanner(text: string, ms: number, fill: number = COLOURS.gold, where: 'middle' | 'top' = 'middle'): Promise<void> {
    const b = this.banner;
    b.text = text;
    const size = Math.round(Math.min(where === 'top' ? 40 : 72, this.gridBox.w * (where === 'top' ? 0.12 : 0.22)));
    b.style = displayStyle(size, fill);
    b.position.set(this.gridBox.x + this.gridBox.w / 2, where === 'top' ? this.gridBox.y + size * 0.4 : this.gridBox.y + this.gridBox.h / 2);
    b.visible = true;
    b.alpha = 1;
    await animate(Math.min(260, ms / 2), (t) => b.scale.set(1.6 - 0.6 * ease.outBack(t)));
    await animate(ms - Math.min(260, ms / 2), (t) => (b.alpha = t < 0.6 ? 1 : 1 - (t - 0.6) / 0.4));
    if (b.text === text) b.visible = false;
  }

  /** "+1", the streak or PRIZE UP! rising from a hole. */
  private float(h: HoleView, text: string, big: boolean): void {
    const f = new Text({ text, style: big ? displayStyle(22) : displayStyle(16, COLOURS.cream) });
    f.anchor.set(0.5);
    // Over the mole's head, but never up into the prize ladder or past the
    // board's sides.
    const half = f.width / 2;
    const x = Math.min(Math.max(h.root.x, half), Math.max(half, this.contentWidth - half));
    f.position.set(x, Math.max(this.gridBox.y + 28, h.root.y - h.moleH * 0.7));
    this.floaters.addChild(f);
    const y0 = f.y;
    void animate(prefersReducedMotion() ? 300 : 650, (t) => {
      f.y = y0 - 16 * ease.outCubic(t);
      f.alpha = t < 0.6 ? 1 : 1 - (t - 0.6) / 0.4;
    }).then(() => f.destroy());
  }

  private secondsLeft(): number {
    if (this.stage === 'ready' || this.stage === 'countdown') return Math.round(this.setup.minMs / 1000);
    if (this.stage !== 'round') return 0;
    return Math.max(0, Math.ceil((this.end - (performance.now() - this.t0)) / 1000));
  }

  private paintCounters(): void {
    if (!this.counters.visible) return;
    this.counters.set(
      [String(this.hit.size), bandPay(this.setup, this.hit.size).toLocaleString('en-GB'), `${this.secondsLeft()}s`],
      this.contentWidth,
    );
  }

  protected leadText(): string {
    const over = this.game.phase === 'over';
    return whackLead(over ? 'over' : this.stage, this.offer?.setup, over ? this.serverHits() : this.hit.size);
  }

  protected pieceLabels(): string[] {
    const t = performance.now() - this.t0;
    return this.holes.map((_, i) => {
      if (this.stage !== 'round') return `Hole ${i + 1}`;
      const s = holeAt(this.pops, i, t, this.hitAt);
      if (s.pop < 0) return `Hole ${i + 1}`;
      return s.dazed ? `Hole ${i + 1}: hit` : `Hole ${i + 1}: up`;
    });
  }

  protected renderPieces(y: number, width: number, maxHeight: number): number {
    const over = this.game.phase === 'over';
    y += 6;
    this.meter.position.set(0, y);
    if (Math.abs(this.meterWidth - width) > 1) {
      this.meterWidth = width;
      this.meter.build(this.setup.bands, this.setup.pays, width);
      this.meter.set(over ? Math.min(this.serverHits(), this.setup.bands[this.setup.bands.length - 1] ?? 0) : this.hit.size);
    }
    y += this.meter.rowHeight + 6;
    const countersH = over ? 0 : this.counters.rowHeight + 6;
    const n = this.holes.length;
    const { cells, size, h: cellH, height } = gridCells(n, 3, width, 124, 6, 0.8, maxHeight - this.meter.rowHeight - 12 - countersH);
    this.gridBox = { x: 0, y, w: width, h: height };
    const holeTex = this.deps.texture('road:whack_hole');
    const moleTex = this.deps.texture('road:whack_mole');
    this.holes.forEach((hv, i) => {
      hv.root.position.set(cells[i]!.x, y + cells[i]!.y);
      hv.root.hitArea = { contains: (px: number, py: number) => Math.abs(px) <= size / 2 && Math.abs(py) <= cellH / 2 };
      const holeW = size * 0.94;
      const holeH = holeW / 2;
      const holeY = cellH / 2 - holeH / 2;
      hv.drawn.clear();
      if (holeTex) {
        hv.hole.texture = holeTex;
        hv.hole.width = holeW;
        hv.hole.height = holeH;
        hv.hole.position.set(0, holeY);
        hv.hole.visible = true;
      } else {
        hv.hole.visible = false;
        hv.drawn.ellipse(0, holeY, holeW * 0.42, holeH * 0.34).fill({ color: 0x3a2412 }).stroke({ color: 0x0b1838, width: 3 });
      }
      // The opening, in the cell's space.
      const oy = holeY + (OPENING.cy - 0.5) * holeH;
      const rx = OPENING.rx * holeW;
      const ry = OPENING.ry * holeH;
      hv.moleH = Math.min(size * 0.66, cellH * 0.82);
      hv.moleY = oy + ry * 0.55;
      hv.mask.clear().rect(-size / 2, -cellH, size, cellH + oy).fill(0xffffff).ellipse(0, oy, rx, ry).fill(0xffffff);
      if (moleTex && hv.mole.texture.width <= 1) hv.mole.texture = moleTex;
      hv.mole.width = hv.mole.height = hv.moleH;
      hv.moleDrawn
        .clear()
        .ellipse(0, -hv.moleH * 0.42, hv.moleH * 0.32, hv.moleH * 0.42)
        .fill({ color: 0x8a5a32 })
        .stroke({ color: 0x0b1838, width: 3 });
      hv.root.cursor = this.stage === 'round' ? 'pointer' : 'default';
    });
    if (this.stage !== 'round') this.holes.forEach((hv) => this.showMole(hv, 0, false));
    y += height + 6;
    this.counters.visible = !over;
    if (!over) {
      // While the round plays there's no button: the counters sit in its
      // room, as a scoreboard, rather than leave a gap under the field.
      this.counters.position.set(0, y + (this.keepButtonRoom() ? 26 : 0));
      this.paintCounters();
      y += countersH;
    }
    return y;
  }
}
